import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { BarChart3, Star } from 'lucide-react'
import { useCourseFeedbacks } from '@/services/admin/metrics.service'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatBrDate } from '@/components/super-admin/badges/format'
import { DashboardFilters as Filters } from './DashboardFilters'
import { HeroKpis } from './HeroKpis'
import { ModuleFunnelChart } from './ModuleFunnelChart'
import { BadgesDistributionChart } from './BadgesDistributionChart'
import { ActivityTimelineChart } from './ActivityTimelineChart'
import { ActivityHeatmap } from './ActivityHeatmap'
import { TopUsersRanking } from './TopUsersRanking'
import { CompaniesAnalyticsTable } from './CompaniesAnalyticsTable'
import { WorkbookInteractionsCard } from './WorkbookInteractionsCard'
import { CompletionTimeCard } from './CompletionTimeCard'
import { ModuleCompletionList } from './ModuleCompletionList'
import { QuizScoreByModule, QuizAttemptsList } from './QuizAnalyticsCards'
import type { DashboardFilters as DashboardFiltersType } from './types'

/**
 * Dashboard analítico do super_admin — orchestrator dos sub-componentes.
 *
 * Estado: filtros globais (empresa, curso, date range). Toda mudança
 * propaga pros componentes filhos via prop `filters`.
 *
 * Default: últimos 30 dias, todas empresas, todos cursos.
 */
export function DashboardSuperAdmin() {
  const [filters, setFilters] = useState<DashboardFiltersType>(() => ({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    dateTo: new Date(),
  }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Header com ícone */}
      <div className="flex items-center gap-3 mb-2">
        <div className="rounded-xl p-2 bg-gradient-to-br from-primary/20 to-amber-400/10">
          <BarChart3 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold tracking-tight">Análise</h2>
          <p className="text-xs text-muted-foreground">
            Visão de produto · usuários, engajamento, badges, empresas
          </p>
        </div>
      </div>

      <Filters filters={filters} onChange={setFilters} />

      <HeroKpis filters={filters} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ModuleFunnelChart filters={filters} />
        <BadgesDistributionChart filters={filters} />
      </div>

      <ActivityTimelineChart filters={filters} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ActivityHeatmap filters={filters} className="lg:col-span-2" />
        <TopUsersRanking filters={filters} />
      </div>

      <CompaniesAnalyticsTable
        filters={filters}
        onDrillDown={(companyId) => setFilters({ ...filters, companyIds: [companyId] })}
      />

      {/* Métricas resgatadas do dashboard legado — feedback do user
          2026-05-15: "não era pra os antigos deixarem de existir". */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CompletionTimeCard filters={filters} />
        <WorkbookInteractionsCard filters={filters} />
        <ModuleCompletionList filters={filters} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuizScoreByModule filters={filters} />
        <QuizAttemptsList filters={filters} />
      </div>

      <FeedbackBlock filters={filters} />
    </motion.div>
  )
}

/**
 * Sub-bloco de avaliações dos cursos. Refactor do bloco antigo (que
 * mostrava distribuição estrelas + LISTA de comentários — esse último
 * tinha sumido na v1 do novo dashboard, restaurado em 2026-05-15 por
 * feedback do user).
 *
 * Estratégia: distribuição em coluna esquerda, lista paginada de
 * comentários (50 por vez, "ver mais") na direita.
 */
const PAGE_SIZE = 50

function FeedbackBlock({ filters }: { filters: DashboardFiltersType }) {
  // Avaliações são cumulativas — historicamente acumulam por curso,
  // não fazem sentido em janela de "últimos 30 dias". Ignoramos
  // dateFrom/dateTo aqui (respeitamos só courseId/companyId).
  const { data, isLoading } = useCourseFeedbacks({
    courseId: filters.courseId,
    companyId: filters.companyIds,
  })

  // Quantos comentários estão sendo mostrados (incrementa de 50 em 50)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const listRef = useRef<HTMLDivElement>(null)

  // Reseta paginação quando filtros mudam (novo conjunto de dados)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
    listRef.current?.scrollTo({ top: 0 })
  }, [filters.courseId, filters.companyIds])

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.totalFeedbacks === 0) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Avaliações dos cursos</CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Sem feedbacks pra esse filtro ainda.
        </CardContent>
      </Card>
    )
  }

  const maxCount = Math.max(...Object.values(data.distribution))
  const allFeedbacks = data.feedbacks ?? []
  const visibleFeedbacks = allFeedbacks.slice(0, visibleCount)
  const hasMore = visibleCount < allFeedbacks.length

  return (
    <Card className="glass-card border-border/50">
      <CardHeader>
        <CardTitle className="text-base">Avaliações dos cursos</CardTitle>
        <CardDescription>
          Média {data.averageRating.toFixed(2)} ★ · {data.totalFeedbacks} avaliações ·{' '}
          <span className="opacity-70">histórico completo (ignora filtro de data)</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Distribuição (col-span 2) */}
          <div className="lg:col-span-2 space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Distribuição
            </p>
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = data.distribution[stars] ?? 0
              const pct = maxCount > 0 ? (count / maxCount) * 100 : 0
              return (
                <div key={stars} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-right tabular-nums">{stars}★</span>
                  <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-amber-400 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-12 text-right text-muted-foreground tabular-nums text-xs">
                    {count}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Lista de comentários (col-span 3) */}
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Comentários ({allFeedbacks.length})
            </p>
            <div ref={listRef} className="space-y-3 max-h-[500px] overflow-y-auto pr-2 -mr-2">
              {visibleFeedbacks.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Sem comentários textuais.
                </p>
              ) : (
                visibleFeedbacks.map((f) => (
                  <div
                    key={f.id}
                    className="border border-border/40 rounded-lg p-3 hover:bg-card/30 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage src={f.userAvatar ?? undefined} />
                        <AvatarFallback className="text-[10px]">
                          {f.userName?.slice(0, 2).toUpperCase() ?? '??'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium truncate">{f.userName}</span>
                            <span className="inline-flex items-center gap-0.5 text-amber-400 text-xs">
                              {Array.from({ length: f.rating }, (_, i) => (
                                <Star key={i} className="w-3 h-3 fill-current" strokeWidth={0} />
                              ))}
                            </span>
                          </div>
                          <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                            {formatBrDate(f.createdAt)}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                          {f.courseName}
                        </p>
                        {f.comment && (
                          <p className="text-xs text-foreground/90 mt-1.5 leading-relaxed whitespace-pre-wrap">
                            {f.comment}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="text-xs"
                >
                  Carregar mais {Math.min(PAGE_SIZE, allFeedbacks.length - visibleCount)} ·{' '}
                  {visibleCount} de {allFeedbacks.length}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
